import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { et } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PopoverContent } from "@/components/ui/popover";
import { UserAvatar } from "@/components/ui/user-avatar";
import type { AttributeSourceUser } from "@/hooks/buildings/useBuildingAttributes";
import type { BuildingDataWidgetSections } from "@/lib/i18n/types/buildings-sections/building-data-widget";
import { isConfirmedSource, toAvatarUser, type HeaderFieldSpec } from "./fields";

/** Localized "3 hours ago" for the edit timestamp; null when it is absent or unparseable. */
function relativeEditedAt(editedAt: string | null, language: string) {
  if (!editedAt) return null;
  try {
    return formatDistanceToNow(new Date(editedAt), {
      addSuffix: true,
      locale: language === "et" ? et : undefined,
    });
  } catch {
    return null;
  }
}

/** "Edited by {name} · {time}" line with avatar, shown inside the edit popover
 * when the current value was entered by a user — nothing otherwise. */
function EditedByLine(props: {
  field: HeaderFieldSpec;
  tw: BuildingDataWidgetSections;
  language: string;
}) {
  const { field, tw, language } = props;
  const { source, sourceUser } = field;
  if (!sourceUser) return null;
  const relTime = relativeEditedAt(sourceUser.editedAt, language);

  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <UserAvatar user={toAvatarUser(sourceUser)} size={16} />
      <span className="min-w-0 truncate">
        {isConfirmedSource(source) ? tw.confirmedBy : tw.editedBy}: {sourceUser.name}
        {relTime ? ` · ${relTime}` : ""}
      </span>
    </div>
  );
}

/** Text control for the non-enum fields, with the optional unit suffix inside it. */
function FieldTextInput(props: {
  field: HeaderFieldSpec;
  draft: string;
  isValid: boolean;
  onDraftChange: (value: string) => void;
  onEnter: () => void;
}) {
  const { field, draft, isValid, onDraftChange, onEnter } = props;

  return (
    <div className="relative">
      <Input
        value={draft}
        onChange={(e) => onDraftChange(e.target.value)}
        inputMode={field.inputMode}
        aria-invalid={!isValid}
        className={`h-8 text-[13px] ${field.suffix ? "pr-9" : ""}`}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          onEnter();
        }}
      />
      {field.suffix && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          {field.suffix}
        </span>
      )}
    </div>
  );
}

function PopoverActions(props: {
  saving: boolean;
  canSave: boolean;
  onSave: () => void;
  onCancel: () => void;
  saveLabel: string;
  cancelLabel: string;
}) {
  const { saving, canSave, onSave, onCancel, saveLabel, cancelLabel } = props;

  return (
    <div className="flex items-center justify-end gap-2 pt-0.5">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 px-2.5 text-xs"
        onClick={onCancel}
        disabled={saving}
      >
        {cancelLabel}
      </Button>
      <Button
        type="button"
        size="sm"
        className="h-7 px-3 text-xs"
        onClick={onSave}
        disabled={!canSave}
      >
        {saveLabel}
      </Button>
    </div>
  );
}

interface FieldEditPopoverProps {
  field: HeaderFieldSpec;
  saving: boolean;
  /** Receives the raw draft — the caller normalizes and persists it. */
  onSave: (value: string) => void;
  onCancel: () => void;
  tw: BuildingDataWidgetSections;
  language: string;
  saveLabel: string;
  cancelLabel: string;
}

/** One field's anchored edit popover: label, pre-filled control, optional
 * "Edited by" line, Cancel + Save. Enter saves (text fields), Esc / outside
 * click cancel via Radix. Mounted only while open, so the draft resets on
 * every open. */
export function FieldEditPopover(props: FieldEditPopoverProps) {
  const { field, saving, onSave, onCancel, tw, language, saveLabel, cancelLabel } = props;
  const [draft, setDraft] = useState(field.initialValue);
  const isValid = field.validate(draft);
  const canSave = isValid && !saving;

  const handleSave = () => {
    if (!canSave) return;
    // Nothing typed — close without a pointless write.
    if (draft === field.initialValue) {
      onCancel();
      return;
    }
    onSave(draft);
  };

  return (
    <PopoverContent align="start" sideOffset={6} className={`${field.widthClass ?? "w-64"} p-3`}>
      <div className="flex flex-col gap-2.5">
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {field.label}
        </span>
        <FieldTextInput
          field={field}
          draft={draft}
          isValid={isValid}
          onDraftChange={setDraft}
          onEnter={handleSave}
        />
        <EditedByLine field={field} tw={tw} language={language} />
        <PopoverActions
          saving={saving}
          canSave={canSave}
          onSave={handleSave}
          onCancel={onCancel}
          saveLabel={saveLabel}
          cancelLabel={cancelLabel}
        />
      </div>
    </PopoverContent>
  );
}
